import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { FileText, Scale, AlertTriangle, CheckCircle, Globe } from "lucide-react";

export default function TermsPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/terms`;

  const schemas = [
    webpageSchema(
      "Terms of Service | ExpertAdvise.in",
      "ExpertAdvise.in terms of service govern the use of our insurance and financial advisory platform. Read our disclaimer, user conduct, and intellectual property policies.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Terms of Service", url: pageUrl },
    ]),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Terms of Service | ExpertAdvise.in"
        description="ExpertAdvise.in terms of service govern the use of our insurance and financial advisory platform. Read our disclaimer, user conduct, and intellectual property policies."
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: "Terms of Service" },
              ]}
              className="justify-center mb-4"
            />
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Legal
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">Terms of Service</h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              By using ExpertAdvise.in, you agree to these Terms of Service. Please read them carefully before accessing our platform.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 space-y-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">1. Nature of Service</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed pl-[52px]">
                ExpertAdvise.in is an insurance and financial advisory platform. We provide information, comparisons, and recommendations to help users make informed decisions. We are not an insurance company, broker, or mutual fund distributor. Final purchase decisions are entirely the user's responsibility.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <Scale className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">2. Advisory Disclaimer</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed pl-[52px]">
                All recommendations provided on this platform are advisory in nature and based on the information you provide. We do not guarantee approval of any insurance application, claim settlement, or investment returns. Users are advised to verify all product details with the respective insurance company or fund house before purchase.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">3. No Liability for Third-Party Products</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed pl-[52px]">
                ExpertAdvise.in shall not be liable for any disputes, claim rejections, service issues, or losses arising from products purchased through our recommendations. All contractual relationships are directly between the user and the insurance company, bank, or mutual fund house.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">4. User Conduct</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed pl-[52px]">
                Users agree to provide accurate information and not use the platform for fraudulent purposes. Misrepresentation of personal, financial, or health information may result in denial of service and legal action where applicable.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">5. Intellectual Property</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed pl-[52px]">
                All content on ExpertAdvise.in, including articles, calculators, comparisons, and logos, is the property of ExpertAdvise.in and protected by copyright laws. Unauthorized reproduction, distribution, or commercial use of our content is strictly prohibited.
              </p>
            </div>

            <div className="pt-6 border-t border-slate-100">
              <p className="text-sm text-slate-500 text-center">
                By continuing to use ExpertAdvise.in, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. For questions, please{" "}
                <Link to="/contact" className="text-primary-600 hover:underline">contact us</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}